import { Button, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import axios from "axios";
import React, { useState } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const SAMPLE_HEADERS = [
  "ISBN",
  "Name",
  "Author",
  "Publisher Name",
  "Currency Name",
  "Category Name",
  "Purchase Price",
  "Sale Price",
  "List Price",
  "Unit Type",
  "Unit Measurement",
  "Quantity",
  "Reorder Quantity",
  "Location Name",
  "SKU",
  "Status",
];
const SAMPLE_ROWS = [
  ["978-0-00-000000-1", "Sample Book One", "Author A", "Publisher One", "INR", "Fiction", "100", "150", "150", "piece", "1", "10", "5", "Default", "SKU001", "true"],
  ["978-0-00-000000-2", "Sample Book Two", "Author B", "Publisher One", "INR", "Non-Fiction", "200", "280", "280", "piece", "1", "0", "3", "Default", "SKU002", "true"],
];

const downloadSampleFormat = () => {
  const ws = XLSX.utils.aoa_to_sheet([SAMPLE_HEADERS, ...SAMPLE_ROWS]);
  const colWidths = SAMPLE_HEADERS.map((_, i) => ({ wch: Math.min(18, (SAMPLE_HEADERS[i] || "").length + 2) }));
  ws["!cols"] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Products");
  XLSX.writeFile(wb, "product_import_format.xlsx");
};

const normalizeKey = (key) => {
  if (!key || typeof key !== "string") return "";
  const k = key.toString().trim().toLowerCase().replace(/\s+/g, "_");
  const map = {
    isbn: "isbn",
    name: "name",
    title: "name",
    author: "author",
    book_publisher_id: "book_publisher_id",
    publisher_name: "publisher_name",
    publisher: "publisher_name",
    product_currency_id: "product_currency_id",
    currency_name: "currency_name",
    product_currency: "currency_name",
    currency: "currency_name",
    product_category_id: "product_category_id",
    product_category_name: "product_category_name",
    category_name: "product_category_name",
    product_category: "product_category_name",
    category: "product_category_name",
    purchase_price: "purchase_price",
    sale_price: "sale_price",
    unit_type: "unit_type",
    unit_measurement: "unit_measurement",
    quantity: "quantity",
    reorder_quantity: "reorder_quantity",
    list_price: "list_price",
    location_name: "location_name",
    location: "location_name",
    sku: "sku",
    status: "status",
  };
  return map[k] || key.trim();
};

const parseCSV = (text) => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];
  const headerLine = lines[0];
  const headers = headerLine.split(",").map((h) => normalizeKey(h.trim()));
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(",").map((v) => v.trim());
    const obj = {};
    headers.forEach((h, idx) => {
      if (h) obj[h] = values[idx] != null ? values[idx] : "";
    });
    if (obj.isbn || obj.name) rows.push(obj);
  }
  return rows;
};

const parseExcel = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheet];
        const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
        if (!json.length) {
          resolve([]);
          return;
        }
        const rawHeaders = json[0].map((h) => (h != null ? String(h) : ""));
        const headers = rawHeaders.map((h) => normalizeKey(h));
        const rows = [];
        for (let i = 1; i < json.length; i++) {
          const row = json[i];
          const obj = {};
          headers.forEach((h, idx) => {
            if (h) obj[h] = row[idx] != null ? String(row[idx]).trim() : "";
          });
          if (obj.isbn || obj.name) rows.push(obj);
        }
        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
};

const toBool = (v) => {
  if (v === true || v === "true" || v === "1" || v === "yes" || v === "y") return true;
  if (v === false || v === "false" || v === "0" || v === "no" || v === "n") return false;
  return true;
};

const UploadProductExcel = ({ onSuccess }) => {
  const [loader, setLoader] = useState(false);
  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      message.warning("Please select a CSV or Excel file.");
      return;
    }
    const fileName = (file.name || "").toLowerCase();
    const isCsv = fileName.endsWith(".csv");
    const isExcel = fileName.endsWith(".xlsx") || fileName.endsWith(".xls");
    if (!isCsv && !isExcel) {
      message.warning("Please upload a .csv, .xlsx, or .xls file.");
      return;
    }

    setLoader(true);
    try {
      let rows = [];
      if (isCsv) {
        const text = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = rej;
          r.readAsText(file);
        });
        rows = parseCSV(text);
      } else {
        rows = await parseExcel(file);
      }

      const payload = rows
        .map((row) => {
          const isbn = String(row.isbn || "").trim();
          const name = row.name != null ? String(row.name).trim() : null;
          const author = row.author != null ? String(row.author).trim() : null;
          const publisher_name = (row.publisher_name || row.publisher) && String(row.publisher_name || row.publisher).trim() ? String(row.publisher_name || row.publisher).trim() : null;
          const book_publisher_id = row.book_publisher_id != null && row.book_publisher_id !== "" && !isNaN(Number(row.book_publisher_id)) ? Number(row.book_publisher_id) : null;
          const currency_name = (row.currency_name || row.product_currency || row.currency) && String(row.currency_name || row.product_currency || row.currency).trim() ? String(row.currency_name || row.product_currency || row.currency).trim() : null;
          const product_currency_id = row.product_currency_id != null && row.product_currency_id !== "" && !isNaN(Number(row.product_currency_id)) ? Number(row.product_currency_id) : null;
          const product_category_name = (row.product_category_name || row.product_category || row.category_name || row.category) && String(row.product_category_name || row.product_category || row.category_name || row.category).trim() ? String(row.product_category_name || row.product_category || row.category_name || row.category).trim() : null;
          const product_category_id = row.product_category_id != null && row.product_category_id !== "" && !isNaN(Number(row.product_category_id)) ? Number(row.product_category_id) : null;
          const purchase_price = parseFloat(row.purchase_price);
          const sale_price = parseFloat(row.sale_price);
          const unit_type = row.unit_type != null ? String(row.unit_type).trim() : null;
          const unit_measurement = row.unit_measurement != null ? parseFloat(row.unit_measurement) : null;
          const quantity = parseInt(row.quantity, 10);
          const reorder_quantity = row.reorder_quantity != null && row.reorder_quantity !== "" ? parseInt(row.reorder_quantity, 10) : null;
          const list_price = row.list_price != null && row.list_price !== "" ? parseFloat(row.list_price) : null;
          const location_name = (row.location_name || row.location) && String(row.location_name || row.location).trim() ? String(row.location_name || row.location).trim() : null;
          const sku = row.sku != null ? String(row.sku).trim() : null;
          const status = toBool(row.status);
          const item = {
            isbn: isbn || undefined,
            name: name || undefined,
            author: author || undefined,
            purchase_price: isNaN(purchase_price) ? 0 : purchase_price,
            sale_price: isNaN(sale_price) ? 0 : sale_price,
            unit_type: unit_type || undefined,
            unit_measurement: unit_measurement != null && !isNaN(unit_measurement) ? unit_measurement : undefined,
            quantity: isNaN(quantity) ? 0 : quantity,
            reorder_quantity: reorder_quantity != null && !isNaN(reorder_quantity) ? reorder_quantity : undefined,
            sku: sku || undefined,
            status,
          };
          if (book_publisher_id) item.book_publisher_id = book_publisher_id;
          else if (publisher_name) item.publisher_name = publisher_name;
          if (product_currency_id) item.product_currency_id = product_currency_id;
          else if (currency_name) item.product_currency_name = currency_name;
          if (product_category_id) item.product_category_id = product_category_id;
          else if (product_category_name) item.product_category_name = product_category_name;
          if (list_price != null && !isNaN(list_price)) item.list_price = list_price;
          if (location_name) item.location_name = location_name;
          return item;
        })
        .filter((r) => r.isbn && (r.book_publisher_id || r.publisher_name) && (r.product_currency_id || r.product_currency_name));

      if (payload.length === 0) {
        message.warning(
          "No valid rows. Each row needs: ISBN, Name, Publisher Name (or ID), Currency Name (or ID), Sale Price. Category Name is optional."
        );
        setLoader(false);
        return;
      }

      const { data } = await axios.post("product?query=createmany", payload);
      setLoader(false);
      setFile(null);
      e.target.reset();

      if (data.count !== undefined) {
        toast.success(`Imported ${data.count} product(s) successfully.`);
      } else {
        toast.success(`Imported ${payload.length} product(s) successfully.`);
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      setLoader(false);
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || "Import failed.";
      toast.error(msg);
    }
  };

  return (
    <div className="text-center mt-2">
      <Button
        type="default"
        icon={<DownloadOutlined />}
        onClick={downloadSampleFormat}
        className="mb-2"
      >
        Download sample format (.xlsx)
      </Button>
      <form onSubmit={handleSubmit}>
        <input
          className="form-control"
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileChange}
        />
        <br />
        <Button className="mt-2" type="primary" htmlType="submit" shape="round" loading={loader}>
          Import from Excel
        </Button>
      </form>
    </div>
  );
};

export default UploadProductExcel;
